import type { CSSProperties, ReactNode } from 'react';
import { useRevealOnScroll } from '@/hooks/useRevealOnScroll';

/**
 * 滚动进入视口时淡入。`delay` 用来做同级卡片的依次出现，
 * 超过 6 张之后不再叠加延迟 —— 等太久比不动效更糟。
 */
export function Reveal({
  children,
  delay = 0,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: 'div' | 'li' | 'section';
}) {
  const ref = useRevealOnScroll<HTMLDivElement>();

  return (
    <Tag
      ref={ref as never}
      className={['reveal', className].filter(Boolean).join(' ')}
      style={delay ? ({ '--reveal-delay': `${Math.min(delay, 6) * 60}ms` } as CSSProperties) : undefined}
    >
      {children}
    </Tag>
  );
}

/** 大号数字：首页的硬指标、单机页的读数都用它，等宽数字避免跳动。 */
export function Stat({
  value,
  unit,
  label,
  hint,
  size = 'md',
  className,
}: {
  value: string;
  unit?: string;
  label: string;
  hint?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}) {
  const valueSize = size === 'lg' ? 'text-4xl md:text-5xl' : size === 'sm' ? 'text-xl' : 'text-3xl';

  return (
    <div className={['flex flex-col gap-1.5', className].filter(Boolean).join(' ')}>
      <div className="flex items-baseline gap-1.5">
        <span className={`metric-value ${valueSize} text-fg`}>{value}</span>
        {unit ? <span className="metric-value text-sm text-fg-muted">{unit}</span> : null}
      </div>
      <span className="text-sm font-medium text-fg">{label}</span>
      {hint ? <span className="text-xs leading-relaxed text-fg-muted">{hint}</span> : null}
    </div>
  );
}

/** 指标网格的分隔线靠边框做，避免每张卡片都包一层。 */
export function StatGrid({
  children,
  columns = 2,
  className,
}: {
  children: ReactNode;
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  const cols =
    columns === 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : columns === 3
        ? 'sm:grid-cols-2 lg:grid-cols-3'
        : 'sm:grid-cols-2';

  return <div className={['grid grid-cols-1 gap-6', cols, className].filter(Boolean).join(' ')}>{children}</div>;
}
